import { badgeMarkup } from '../../ui/routines.js';
/**
 * Kanban View Module
 * Pure view: receives state, emits events via callbacks.
 * Columns are based on tags (stored in CRDT metadata as kanbanColumns).
 * Default columns: Todo, In Progress, Done (mandatory tags).
 * Nodes can appear in multiple columns if they have multiple matching tags.
 */

import * as Store from "../../store/index.js";
import { renderHorizonControl } from "../../ui/horizon-control.js";
import { visibleViewRect, centeringOffset } from '../../ui/view-centering.js';
import { safeColor, escapeAttr } from "../../../../engine/core/validation.js";
import { setupNotePreviewOnBadge } from "../../ui/note-preview.js";
import { getLinkedBadge, linkedStubContainerClasses } from "../linked-badge.js";
import { t } from "../../../../shared/i18n/index.js";
import { viewLabel } from "../../../../engine/core/view-names.js";
import { tagLabel } from "../../../../engine/core/tag-filter.js";


// Module state
let container = null;
let callbacks = null;
let currentState = null;

// Batched rendering
let renderScheduled = false;

// Drag state for mouse-based drag
let dragState = {
  isDragging: false,
  cardEl: null,
  cardId: null,
  sourceTagId: null,
  ghostEl: null,
  offsetX: 0,
  offsetY: 0,
};

// Listener bookkeeping. render() recreates the board via innerHTML on every
// store change, so anything attached to `document` (or the persistent
// container) must be attached exactly once and removed in destroy() —
// otherwise handlers accumulate per render (hundreds after an hour of
// active collaboration) and survive view teardown.
let currentDropTarget = null;
let docListenersAttached = false;
let containerWithHandlers = null;
let containerClickHandler = null;
let containerDblclickHandler = null;
let renderedContext = null;

/**
 * Render the Kanban view
 */
export function render(containerEl, state, cbs) {
  const context = JSON.stringify([state.currentDocId, state.focusId]);
  const sameView = container === containerEl && renderedContext === context;
  const previousBoard = sameView ? containerEl.querySelector('.kanban-board') : null;
  const scrollLeft = previousBoard?.scrollLeft || 0;
  const columnScroll = new Map([...(previousBoard?.querySelectorAll('.kanban-cards') || [])]
    .map(column => [column.dataset.tagId, column.scrollTop]));
  container = containerEl;
  callbacks = cbs;
  currentState = state;
  renderedContext = context;

  const focused = cbs.find(state.focusId) || state.data;
  const tagCtx = buildTagContext(state);
  const allTasks = collectTasks(focused, state, cbs, tagCtx);

  // Get column definitions from CRDT
  const columns = callbacks.getKanbanColumns?.() || [];
  const tagDefs = callbacks.getTagDefinitions?.() || [];
  const tagMap = new Map(tagDefs.map(t => [t.id, t]));

  // Group tasks by tag (a task can appear in multiple columns)
  const grouped = groupByTag(allTasks, columns, tagMap);

  // Available tags not yet used as columns (for "add column" dropdown)
  const usedTagIds = new Set(columns.map(c => c.tagId));
  const availableTags = tagDefs.filter(t => !usedTagIds.has(t.id));

  const boardClass = tagCtx.highlightActive ? "kanban-board tag-filtering" : "kanban-board";
  container.innerHTML = `
    <div class="breadcrumb" id="kanban-crumbs"></div>
    <div class="${boardClass}" id="kanban-board">
      ${columns.map((col, i) => {
        const tag = tagMap.get(col.tagId);
        if (!tag) return '';
        return renderColumn(tag, grouped.get(col.tagId) || [], state, i, columns.length, tagCtx);
      }).join("")}
      <div class="kanban-add-column">
        <button class="kanban-add-column-btn" id="kanban-add-col-btn" title="${t('views.kanban.addColumn')}">+</button>
      </div>
    </div>
  `;

  renderBreadcrumbs();
  setupDragDrop();
  setupColumnControls(columns, availableTags);
  attachEventHandlers();
  container.querySelector('.kanban-board').scrollLeft = scrollLeft;
  container.querySelectorAll('.kanban-cards').forEach(column => {
    column.scrollTop = columnScroll.get(column.dataset.tagId) || 0;
  });
}

/**
 * Collect all leaf tasks (nodes without children).
 *
 * Tag-visibility rules (parity with bubble/gantt): Done-cascade prunes the
 * subtree; otherwise a self-tag-hidden leaf is dropped from the board. A
 * tag-hidden non-leaf parent doesn't appear as a card (kanban only shows
 * leaves), but recursion continues so non-hidden descendants still appear.
 */
function collectTasks(node, state, cbs, tagCtx) {
  const tasks = [];
  const rootId = state.data?.id;
  const { isDoneCascaded, isTagHidden } = tagCtx;

  function traverse(n) {
    if (!n || !n.children) return;
    for (const child of n.children) {
      if (isDoneCascaded(child)) continue;             // prune subtree
      const isLeaf = !child.children || child.children.length === 0;
      if (isLeaf) {
        if (isTagHidden(child)) continue;              // skip tag-hidden leaf
        const parentNode = callbacks.findParent
          ? callbacks.findParent(child.id)
          : null;
        tasks.push({
          ...child,
          parentName: parentNode && parentNode.id !== rootId
            ? parentNode.name || parentNode.text
            : "",
          parentId: parentNode ? parentNode.id : node.id,
        });
      } else {
        traverse(child);
      }
    }
  }

  traverse(node);
  return tasks;
}

/**
 * Build tag visibility + highlight context. Mirrors bubble's rules but for a
 * flat leaf-list (no ancestor-aware "keep parent" — kanban only shows leaves).
 */
function buildTagContext(state) {
  const hiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
  const highlightedTagIds = callbacks?.getHighlightedTagIds?.() || [];
  const doneTagId = callbacks?.getDoneTagId?.() || null;
  const nodeIsDoneFn = callbacks?.nodeIsDone || null;
  const tagFilter = callbacks?.makeTagFilter?.();

  const hide = !!state.hideCompleted;
  const doneHidden = hide || (!!doneTagId && hiddenTagIds.includes(doneTagId));
  const isDone = (n) => n.status === "done" || !!nodeIsDoneFn?.(n);
  const isDoneCascaded = (n) => doneHidden && isDone(n);
  const outOfHorizon = callbacks?.getHorizonPredicate?.() || null;
  const isTagHidden = (n) => {
    if (outOfHorizon && outOfHorizon(n)) return true;
    if (tagFilter?.isHidden(n, hiddenTagIds)) return true;
    return false;
  };

  const isTagMatch = (n) => !!tagFilter?.isMatch(n, highlightedTagIds);

  return {
    isDoneCascaded,
    isTagHidden,
    isTagMatch,
    highlightActive: highlightedTagIds.length > 0,
  };
}

/**
 * Group tasks by tag for each column.
 * A task appears in a column if it has that column's tag.
 */
function groupByTag(tasks, columns, tagMap) {
  const grouped = new Map();
  for (const col of columns) {
    grouped.set(col.tagId, []);
  }

  for (const task of tasks) {
    const taskTagIds = (task.tags || []).map(t => typeof t === 'string' ? t : t.tagId);
    for (const col of columns) {
      if (taskTagIds.includes(col.tagId)) {
        grouped.get(col.tagId).push(task);
      }
    }
  }

  // Sort each column by deadline
  for (const [, tasks] of grouped) {
    tasks.sort((a, b) => {
      if (!a.endDate && !b.endDate) return 0;
      if (!a.endDate) return 1;
      if (!b.endDate) return -1;
      return new Date(a.endDate) - new Date(b.endDate);
    });
  }

  return grouped;
}

/**
 * Render a kanban column
 */
function renderColumn(tag, tasks, state, index, total, tagCtx) {
  const escapeHtml = callbacks.escapeHtml || ((s) => s);

  return `
    <div class="kanban-column" data-tag-id="${escapeAttr(tag.id)}">
      <div class="kanban-column-header">
        <span class="kanban-column-title">${escapeHtml(tagLabel(tag))}</span>
        <span class="kanban-column-count">${tasks.length}</span>
        <div class="kanban-column-actions">
          ${index > 0 ? `<button class="kanban-col-btn kanban-col-move-left" data-tag-id="${escapeAttr(tag.id)}" title="${t('views.kanban.moveLeft')}">\u2190</button>` : ''}
          ${index < total - 1 ? `<button class="kanban-col-btn kanban-col-move-right" data-tag-id="${escapeAttr(tag.id)}" title="${t('views.kanban.moveRight')}">\u2192</button>` : ''}
          ${!tag.mandatory ? `<button class="kanban-col-btn kanban-col-remove" data-tag-id="${escapeAttr(tag.id)}" title="${t('views.kanban.removeColumn')}">\u00D7</button>` : ''}
        </div>
      </div>
      <div class="kanban-cards" data-tag-id="${escapeAttr(tag.id)}">
        ${tasks.length === 0
          ? `<div class="kanban-empty">${t('views.kanban.noTasks')}</div>`
          : tasks.map(task => renderCard(task, state, tagCtx)).join("")
        }
      </div>
    </div>
  `;
}

/**
 * Render a task card
 */
function renderCard(task, state, tagCtx) {
  const isSelected = task.id === state.selectedId;
  const escapeHtml = callbacks.escapeHtml || ((s) => s);

  let dateStr = "";
  if (task.startDate && task.endDate) {
    dateStr = `${task.startDate} \u2192 ${task.endDate}`;
  } else if (task.startDate || task.endDate) {
    dateStr = task.startDate || task.endDate;
  }

  const taskName = task.name || task.text || t("common.untitled");

  const _linked = getLinkedBadge(task);
  const refBadge = _linked.isLinkedStub
    ? `<span class="kanban-ref-badge ${escapeAttr(_linked.className)}" data-id="${escapeAttr(task.id)}" title="${escapeHtml(_linked.title)}">${_linked.icon}</span>`
    : "";
  const stubCardClass = linkedStubContainerClasses(task);

  const notesBadge = task.notes && task.notes.trim()
    ? `<span class="kanban-notes-badge" data-id="${escapeAttr(task.id)}">\uD83D\uDDD2\uFE0F</span>`
    : "";

  const classes = ["kanban-card"];
  if (isSelected) classes.push("selected");
  if (stubCardClass) classes.push(stubCardClass);
  if (tagCtx?.isTagMatch?.(task)) classes.push("tag-match");

  const effectiveStatus = callbacks?.getEffectiveStatus?.(task) || task.status || "todo";

  return `
    <div class="${classes.join(" ")}"
         data-id="${escapeAttr(task.id)}"
         data-status="${escapeAttr(effectiveStatus)}"
         style="border-left-color: ${safeColor(task.color, "#6366f1")}">
      <div class="kanban-card-title">${refBadge}${notesBadge}${escapeHtml(taskName)}${badgeMarkup(task.id)}</div>
      <div class="kanban-card-meta">
        ${dateStr ? `<span class="kanban-card-dates">\uD83D\uDCC5 ${escapeHtml(dateStr)}</span>` : ""}
        ${task.parentName ? `<span class="kanban-card-parent">\uD83D\uDCC1 ${escapeHtml(task.parentName)}</span>` : ""}
      </div>
    </div>
  `;
}

/**
 * Render breadcrumbs
 */
function renderBreadcrumbs() {
  const crumbsEl = container.querySelector("#kanban-crumbs");
  if (!crumbsEl) return;
  renderHorizonControl(container, currentState.horizon, callbacks.onHorizonChange, currentState.currentDocId);

  const path = callbacks.getPath ? callbacks.getPath(currentState.focusId) : [];
  const escapeHtml = callbacks.escapeHtml || ((s) => s);
  const viewIndicator = `<span class="view-indicator">${viewLabel('kanban')}</span>`;

  if (!path || path.length === 0) {
    const rootData = currentState.data;
    crumbsEl.innerHTML = viewIndicator +
      `<div class="crumb current" data-id="${rootData.id}">
        <span class="crumb-dot" style="background:${safeColor(rootData.color, "#6366f1")}"></span>${escapeHtml(rootData.name || t("views.root"))}
      </div>`;
    return;
  }

  crumbsEl.innerHTML = viewIndicator + path.map((node, i) => {
    const isLast = i === path.length - 1;
    const name = node.name || node.text || t("views.root");
    const sep = i > 0 ? '<span class="crumb-sep">\u203A</span>' : "";
    return `${sep}<div class="crumb ${isLast ? "current" : ""}" data-id="${escapeAttr(node.id)}">
      <span class="crumb-dot" style="background:${safeColor(node.color, "#6366f1")}"></span>${escapeHtml(name)}
    </div>`;
  }).join("");

  crumbsEl.querySelectorAll(".crumb:not(.current)").forEach(crumb => {
    crumb.addEventListener("click", () => {
      if (callbacks.onNavigate) callbacks.onNavigate(crumb.dataset.id);
    });
  });
}

/**
 * Setup column controls (add, remove, reorder)
 */
function setupColumnControls(columns, availableTags) {
  // Add column button
  const addBtn = container.querySelector("#kanban-add-col-btn");
  if (addBtn) {
    addBtn.addEventListener("click", () => {
      showAddColumnMenu(addBtn, availableTags, columns);
    });
  }

  // Move and remove buttons
  container.querySelectorAll(".kanban-col-move-left").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      moveColumn(btn.dataset.tagId, -1, columns);
    });
  });
  container.querySelectorAll(".kanban-col-move-right").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      moveColumn(btn.dataset.tagId, 1, columns);
    });
  });
  container.querySelectorAll(".kanban-col-remove").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      removeColumn(btn.dataset.tagId, columns);
    });
  });
}

function showAddColumnMenu(anchorEl, availableTags, currentColumns) {
  // Remove existing menu
  document.querySelector(".kanban-add-col-menu")?.remove();

  if (availableTags.length === 0) return;

  const menu = document.createElement("div");
  menu.className = "kanban-add-col-menu";

  for (const tag of availableTags) {
    const item = document.createElement("button");
    item.className = "kanban-add-col-item";
    item.textContent = tagLabel(tag);
    item.addEventListener("click", () => {
      const newColumns = [...currentColumns, { tagId: tag.id, order: currentColumns.length }];
      callbacks.setKanbanColumns?.(newColumns);
      menu.remove();
      scheduleRender();
    });
    menu.appendChild(item);
  }

  const rect = anchorEl.getBoundingClientRect();
  menu.style.position = "fixed";
  menu.style.left = rect.left + "px";
  menu.style.top = rect.bottom + 4 + "px";
  document.body.appendChild(menu);

  setTimeout(() => {
    const handler = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener("mousedown", handler);
      }
    };
    document.addEventListener("mousedown", handler);
  }, 0);
}

function moveColumn(tagId, direction, columns) {
  const idx = columns.findIndex(c => c.tagId === tagId);
  if (idx === -1) return;
  const newIdx = idx + direction;
  if (newIdx < 0 || newIdx >= columns.length) return;

  const newColumns = [...columns];
  [newColumns[idx], newColumns[newIdx]] = [newColumns[newIdx], newColumns[idx]];
  // Update order field
  newColumns.forEach((c, i) => c.order = i);
  callbacks.setKanbanColumns?.(newColumns);
  scheduleRender();
}

function removeColumn(tagId, columns) {
  const newColumns = columns.filter(c => c.tagId !== tagId);
  newColumns.forEach((c, i) => c.order = i);
  callbacks.setKanbanColumns?.(newColumns);
  scheduleRender();
}

/**
 * Setup mouse-based drag and drop.
 * The board element is recreated by every render(), so its mousedown
 * listener naturally dies with it and is re-attached here each time.
 * The document-level move/up listeners are module-level singletons.
 */
function setupDragDrop() {
  const board = container.querySelector("#kanban-board");
  if (!board) return;

  board.addEventListener("mousedown", (e) => {
    const card = e.target.closest(".kanban-card");
    if (!card) return;
    if (callbacks.isReadOnly && callbacks.isReadOnly()) return;

    e.preventDefault();

    // Find which column this card is in
    const column = card.closest(".kanban-column");
    const sourceTagId = column?.dataset.tagId || null;

    const rect = card.getBoundingClientRect();
    dragState = {
      isDragging: true,
      cardEl: card,
      cardId: card.dataset.id,
      sourceTagId,
      ghostEl: null,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };

    const ghost = card.cloneNode(true);
    ghost.classList.add("kanban-ghost");
    ghost.style.position = "fixed";
    ghost.style.width = rect.width + "px";
    ghost.style.left = e.clientX - dragState.offsetX + "px";
    ghost.style.top = e.clientY - dragState.offsetY + "px";
    ghost.style.pointerEvents = "none";
    ghost.style.zIndex = "9999";
    ghost.style.opacity = "0.9";
    ghost.style.transform = "rotate(2deg)";
    ghost.style.boxShadow = "0 8px 24px rgba(0,0,0,0.3)";
    ghost.style.transition = "none";
    document.body.appendChild(ghost);
    dragState.ghostEl = ghost;

    card.classList.add("dragging");
    currentDropTarget = null;
  });

  if (!docListenersAttached) {
    document.addEventListener("mousemove", onDragMouseMove);
    document.addEventListener("mouseup", onDragMouseUp);
    docListenersAttached = true;
  }
}

function onDragMouseMove(e) {
  if (!dragState.isDragging || !dragState.ghostEl) return;
  dragState.ghostEl.style.left = e.clientX - dragState.offsetX + "px";
  dragState.ghostEl.style.top = e.clientY - dragState.offsetY + "px";

  const target = document.elementFromPoint(e.clientX, e.clientY);
  const column = target?.closest(".kanban-cards");
  if (column !== currentDropTarget) {
    if (currentDropTarget) currentDropTarget.classList.remove("drag-over");
    if (column) column.classList.add("drag-over");
    currentDropTarget = column;
  }
}

function onDragMouseUp(e) {
  if (!dragState.isDragging) return;

  if (dragState.ghostEl) dragState.ghostEl.remove();
  if (dragState.cardEl) dragState.cardEl.classList.remove("dragging");
  if (currentDropTarget) currentDropTarget.classList.remove("drag-over");

  const target = document.elementFromPoint(e.clientX, e.clientY);
  const column = target?.closest(".kanban-cards");
  const targetTagId = column?.dataset.tagId;

  if (dragState.cardId && targetTagId && targetTagId !== dragState.sourceTagId) {
    // 1. Add the target tag. If both tags are mandatory (Todo / In Progress
    //    / Done), toggleNodeTag applies mutual exclusion internally and
    //    will already strip the source tag, leaving us nothing to do in
    //    step 2. For non-mandatory tags the source must be removed
    //    explicitly, but only when it's still present.
    callbacks.toggleNodeTag?.(dragState.cardId, targetTagId);
    if (dragState.sourceTagId) {
      const node = callbacks.find?.(dragState.cardId);
      if (node && callbacks.nodeHasTag?.(node, dragState.sourceTagId)) {
        callbacks.toggleNodeTag?.(dragState.cardId, dragState.sourceTagId);
      }
    }
  }

  dragState = {
    isDragging: false,
    cardEl: null,
    cardId: null,
    sourceTagId: null,
    ghostEl: null,
    offsetX: 0,
    offsetY: 0,
  };
  currentDropTarget = null;
}

/**
 * Attach event handlers
 */
function attachEventHandlers() {
  if (!container) return;

  container.querySelectorAll(".kanban-notes-badge").forEach(badge => {
    const nodeId = badge.dataset.id;
    const node = callbacks.find?.(nodeId);
    if (node?.notes) {
      setupNotePreviewOnBadge(badge, node.notes, callbacks.getDocId?.(), callbacks.getDek?.());
    }
  });

  // The container element persists across renders — attach the delegated
  // click/dblclick handlers once per container, not once per render.
  if (containerWithHandlers === container) return;
  if (containerWithHandlers) detachContainerHandlers();

  containerClickHandler = (e) => {
    const notesBadge = e.target.closest(".kanban-notes-badge");
    if (notesBadge) {
      e.stopPropagation();
      if (callbacks.onOpenModal) callbacks.onOpenModal(notesBadge.dataset.id);
      return;
    }

    const card = e.target.closest(".kanban-card");
    if (card && !dragState.isDragging) {
      if (callbacks.onNodeSelect) callbacks.onNodeSelect(card.dataset.id);
    }
  };

  containerDblclickHandler = (e) => {
    if (callbacks.isReadOnly && callbacks.isReadOnly()) return;
    const card = e.target.closest(".kanban-card");
    if (card && callbacks.onNodeEdit) {
      callbacks.onNodeEdit(card.dataset.id);
    }
  };

  container.addEventListener("click", containerClickHandler);
  container.addEventListener("dblclick", containerDblclickHandler);
  containerWithHandlers = container;
}

function detachContainerHandlers() {
  if (!containerWithHandlers) return;
  if (containerClickHandler) {
    containerWithHandlers.removeEventListener("click", containerClickHandler);
  }
  if (containerDblclickHandler) {
    containerWithHandlers.removeEventListener("dblclick", containerDblclickHandler);
  }
  containerWithHandlers = null;
  containerClickHandler = null;
  containerDblclickHandler = null;
}

function scheduleRender() {
  if (renderScheduled) return;
  renderScheduled = true;
  requestAnimationFrame(() => {
    renderScheduled = false;
    if (container && callbacks && currentState) {
      render(container, currentState, callbacks);
    }
  });
}

/** Bring the nearest copy of a task into view, scrolling its column as well. */
export function centerOnNode(nodeId) {
  const board = container?.querySelector('.kanban-board');
  const visible = visibleViewRect(board);
  if (!visible) return false;
  const cards = [...board.querySelectorAll(`.kanban-card[data-id="${CSS.escape(nodeId)}"]`)];
  const distance = card => {
    const rect = card.getBoundingClientRect();
    return Math.abs(rect.left + rect.width / 2 - visible.x);
  };
  const card = cards.sort((a, b) => distance(a) - distance(b))[0];
  if (!card) return false;
  board.scrollLeft -= centeringOffset(board, card).x;
  const column = card.closest('.kanban-cards');
  const offset = centeringOffset(column, card);
  if (!offset) return false;
  column.scrollTop -= offset.y;
  return true;
}

export function update(prevState, nextState) {
  currentState = nextState;
  if (container && callbacks) {
    scheduleRender();
  }
}

export function destroy() {
  renderedContext = null;
  if (docListenersAttached) {
    document.removeEventListener("mousemove", onDragMouseMove);
    document.removeEventListener("mouseup", onDragMouseUp);
    docListenersAttached = false;
  }
  detachContainerHandlers();
  if (dragState.ghostEl) dragState.ghostEl.remove();
  dragState = { isDragging: false, cardEl: null, cardId: null, sourceTagId: null, ghostEl: null, offsetX: 0, offsetY: 0 };
  currentDropTarget = null;
  if (container) container.innerHTML = "";
  container = null;
  callbacks = null;
  currentState = null;
  renderScheduled = false;
}

export const viewInfo = {
  name: "kanban",
  get label() { return viewLabel("kanban"); },
  icon: "columns",
};
